import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import SEO from "@/components/seo/SEO";
import { buildMenuLd } from "@/lib/jsonld";
import { useContenido } from "@/context/ContenidoContext";

function Precio({ p }) {
    if (!p) return null;
    return (
        <span className="font-display italic text-gold whitespace-nowrap text-xl">
            {String(p).includes("€") || p === "consultar" ? p : `${p} €`}
        </span>
    );
}

function ItemRow({ nombre, descripcion, precio, estrella, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.7, delay: Math.min(index * 0.03, 0.4) }}
            className="py-6 border-b border-gold/10 last:border-0"
        >
            <div className="flex items-baseline gap-2">
                <h4 className="font-display text-2xl md:text-3xl text-ivory flex items-center gap-2 font-light">
                    {nombre}
                    {estrella && (
                        <Star size={14} className="text-gold ml-1" fill="currentColor" strokeWidth={0} />
                    )}
                </h4>
                <span className="menu-leader hidden md:block" />
                <Precio p={precio} />
            </div>
            {descripcion && (
                <p className="font-editorial italic text-sm text-stone mt-2 leading-relaxed max-w-2xl">
                    {descripcion}
                </p>
            )}
        </motion.div>
    );
}

const GALERIA = [
    { nombre: "chuleton-brasa", alt: "Chuletón a la brasa - Restaurante Museo Tenería Ocaña" },
    { nombre: "pato-frutos-rojos", alt: "Terrina de pato con frutos rojos - Tenería Ocaña" },
    { nombre: "cordero-pure", alt: "Cordero con puré - Restaurante Tenería Ocaña" },
    { nombre: "venado-frutos-rojos", alt: "Bocaditos de venado con frutos rojos - Tenería Ocaña" },
    { nombre: "cochinillo-naranja", alt: "Terrina de cochinillo con naranja - Tenería Ocaña" },
    { nombre: "ensalada-salmon", alt: "Ensalada con salmón marinado - Restaurante Tenería Ocaña" },
    { nombre: "alcachofas-foie", alt: "Alcachofas confitadas con foie - Tenería Ocaña" },
    { nombre: "terraza-barricas", alt: "Terraza patio interior con barricas - Restaurante Tenería" },
    { nombre: "salon-principal", alt: "Salón principal del Restaurante Museo Tenería Ocaña" },
    { nombre: "fachada-nocturna", alt: "Fachada nocturna del Restaurante Museo Tenería en Ocaña" },
    { nombre: "artesonado", alt: "Artesonado original del siglo XV - Museo Tenería Ocaña" },
    { nombre: "horno-lena", alt: "Horno de leña artesanal - Restaurante Museo Tenería Ocaña" },
    { nombre: "cueva", alt: "Cueva original del siglo XV - Museo Tenería Ocaña" },
    { nombre: "tinaja", alt: "Tinaja cerámica original - Museo Tenería Ocaña" },
    { nombre: "entrada-museo", alt: "Entrada al Museo Tenería con bargueño español" },
    { nombre: "arcón-epoca", alt: "Arcón original de época - Museo Tenería Ocaña" },
    { nombre: "mecedora-cuero", alt: "Mecedora de cuero antigua - Museo Tenería Ocaña" },
    { nombre: "calle-ocana", alt: "Calle típica de Ocaña decorada junto a la Tenería" },
];

export default function Gastronomia() {
    const { t } = useTranslation();
    const { carta, config } = useContenido();
    const chuleton = config?.chuleton || { precio: "56€/kg", descripcion: "Pregunta por las sugerencias del día." };
    const panPrecio = config?.pan_precio || "1,5";
    const menuLd = buildMenuLd(carta, chuleton.precio);

    return (
        <article className="pt-32 pb-24 paper-grain">
            <SEO
                title={t("gastronomia.seo.title")}
                description={t("gastronomia.seo.description")}
                keywords="carta restaurante Ocaña, chuletón Ocaña, cocina manchega, parrilla Toledo, ibéricos Ocaña, vinos La Mancha, menú restaurante Toledo"
                path="/gastronomia"
                breadcrumbs={[
                    { name: t("nav.home"), item: "/" },
                    { name: t("nav.gastronomia"), item: "/gastronomia" },
                ]}
                jsonLd={menuLd}
            />

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-6 text-center mb-32">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 1 }}
                    className="roman-numeral"
                >
                    — {t("gastronomia.kicker")} —
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display font-light text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] text-ivory mt-8"
                >
                    {t("gastronomia.title1")}
                    <span className="block italic text-gold">{t("gastronomia.title2")}</span>
                </motion.h1>
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 1, duration: 1 }}
                    className="line-gold mx-auto my-10 origin-left"
                />
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8, duration: 1.2 }}
                    className="font-body text-lg text-stone max-w-2xl mx-auto leading-relaxed"
                >
                    {t("gastronomia.subtitle")}
                </motion.p>
            </section>

            {/* Galería de platos */}
            <section className="max-w-6xl mx-auto px-6 mb-32">
                <div className="text-center mb-16">
                    <p className="roman-numeral">— Galería —</p>
                    <h2 className="font-display text-4xl md:text-5xl text-ivory font-light mt-4">
                        Nuestros <span className="italic text-gold">platos y espacios</span>
                    </h2>
                    <div className="line-gold mx-auto mt-8" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {GALERIA.map((foto, i) => (
                        <motion.div
                            key={foto.nombre}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-30px" }}
                            transition={{ duration: 0.7, delay: Math.min(i * 0.05, 0.4) }}
                            className="aspect-square overflow-hidden group cursor-pointer"
                        >
                            <img
                                src={`/images/platos/${foto.nombre}.webp`}
                                alt={foto.alt}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                loading="lazy"
                            />
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Chuletón destacado */}
            <motion.section
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2 }}
                className="max-w-4xl mx-auto px-6 mb-32"
            >
                <div className="relative border border-gold p-12 md:p-16 text-center overflow-hidden">
                    <div className="absolute inset-0 paper-grain pointer-events-none" />
                    <div className="absolute -inset-px border border-gold/40" style={{ borderStyle: "double" }} />
                    <p className="roman-numeral relative">— {t("gastronomia.specialty")} —</p>
                    <h2 className="font-display text-5xl md:text-7xl text-ivory font-light mt-6 leading-tight relative">
                        {t("gastronomia.chuleton")}{" "}
                        <span className="text-gold inline-flex gap-0.5">
                            <Star size={20} fill="currentColor" strokeWidth={0} />
                            <Star size={20} fill="currentColor" strokeWidth={0} />
                        </span>
                    </h2>
                    <p className="font-editorial italic text-stone mt-6 max-w-xl mx-auto text-base relative">
                        {chuleton.descripcion}
                    </p>
                    <p className="font-display italic text-gold text-6xl md:text-7xl mt-10 relative">
                        {chuleton.precio}
                    </p>
                </div>
            </motion.section>

            {/* Secciones de carta */}
            <div className="max-w-4xl mx-auto px-6 space-y-32">
                {carta.length === 0 && (
                    <p className="text-stone italic text-center font-editorial">
                        {t("gastronomia.loading")}
                    </p>
                )}
                {carta.map((sec, idx) => (
                    <section key={sec.id} id={sec.slug}>
                        <motion.header
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 1 }}
                            className="text-center mb-16"
                        >
                            <p className="roman-numeral">
                                — {t("gastronomia.chapterPrefix")} {["I", "II", "III", "IV", "V", "VI"][idx]} —
                            </p>
                            <p className="font-editorial italic text-stone mt-4 text-sm">
                                {sec.subtitulo}
                            </p>
                            <h2 className="font-display text-4xl md:text-6xl text-ivory font-light mt-3">
                                {sec.titulo}
                            </h2>
                            <div className="line-gold mx-auto mt-8" />
                        </motion.header>

                        {sec.tipo === "items" && (
                            <div>
                                {sec.items.map((it, i) => (
                                    <ItemRow key={it.id} {...it} index={i} />
                                ))}
                            </div>
                        )}

                        {sec.tipo === "grupos" && (
                            <div className="space-y-14">
                                {(sec.grupos || []).map((g) => (
                                    <div key={g.grupo}>
                                        <h3 className="font-display italic text-gold text-3xl mb-2">
                                            {g.grupo}
                                        </h3>
                                        <div>
                                            {g.items.map((it, i) => (
                                                <ItemRow key={it.id} {...it} index={i} />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                ))}

                <section className="text-center pt-10">
                    <p className="font-body text-stone text-sm">
                        {t("gastronomia.bread")} ·{" "}
                        <span className="font-display italic text-gold">{panPrecio} €</span>
                    </p>
                    <p className="font-editorial italic text-xs text-stone/70 mt-4">
                        {t("gastronomia.vat")}
                    </p>
                </section>
            </div>
        </article>
    );
}